import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Table,
  Input,
  Space,
  Button,
  App,
  Row,
  Col,
  Select,
  DatePicker,
  InputNumber,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  SearchOutlined,
  EditOutlined
} from '@ant-design/icons';
import { listGenders, listCatalog } from '../../services/catalogs';
import { getClientsReport } from '../../services/clients';
import { MEDIUM_OPTIONS, MONTHS_OPTIONS, DATE_TYPE_OPTIONS } from '../../utils/constants';
import { isoToDMY } from '../../utils/date';
import { exportToExcel } from '../../utils/excel';

const { Title } = Typography;
const { useApp } = App;

const ClientReportPage = () => {
  const navigate = useNavigate();
  const { message } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  const getDefaultFilters = () => ({
    dateType: 'registro', // 'registro' o 'suscripcion'
    dateRange: null, // [dayjs, dayjs] o null
    client: '', // nombre, apellido, correo
    gender: null, // ID del género
    ageMin: null, // edad mínima
    ageMax: null, // edad máxima
    city: null, // ID de ciudad (por ahora null)
    state: null, // ID de estado (por ahora null)
    medium: null, // 'administrador', 'app', 'shop'
    purchases: null, // ID de compras (por ahora null)
    plan: null, // ID de plan (por ahora null)
    birthdayMonth: null, // 1-12 (mes del año)
  });

  const [filters, setFilters] = useState(getDefaultFilters);

  const [genders, setGenders] = useState([]);
  const [states, setStates] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [gendersData, statesData, plansData] = await Promise.all([
          listGenders(),
          listCatalog('states'),
          listCatalog('client_plans'),
        ]);
        setGenders(gendersData || []);
        setStates(statesData || []);
        setPlans(plansData || []);
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        message.error('No se pudieron cargar los catálogos');
      }
    };
    loadCatalogs();
  }, [message]);

  useEffect(() => {
    const loadMunicipalities = async () => {
      if (!filters.state) {
        setMunicipalities([]);
        if (filters.city) {
          setFilters(prev => ({ ...prev, city: null }));
        }
        return;
      }

      setLoadingMunicipalities(true);
      try {
        const municipalitiesData = await listCatalog('municipalities', { stateId: filters.state });
        setMunicipalities(municipalitiesData || []);
        if (filters.city) {
          const cityExists = municipalitiesData?.some(m => m.id === filters.city);
          if (!cityExists) {
            setFilters(prev => ({ ...prev, city: null }));
          }
        }
      } catch (error) {
        console.error('Error al cargar municipios:', error);
        message.error('No se pudieron cargar los municipios');
        setMunicipalities([]);
      } finally {
        setLoadingMunicipalities(false);
      }
    };

    loadMunicipalities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.state, filters.city]);

  const fetchData = useCallback(async (page, pageSize, srt, currentFilters = filters) => {
    setLoading(true);
    try {
      const buildParams = (f, pg, pgSize) => ({
        dateType: f.dateType,
        startDate: f.dateRange?.[0]?.format('YYYY-MM-DD') ?? undefined,
        endDate: f.dateRange?.[1]?.format('YYYY-MM-DD') ?? undefined,
        client: f.client?.trim() || undefined,
        gender: f.gender ?? undefined,
        ageMin: f.ageMin ?? undefined,
        ageMax: f.ageMax ?? undefined,
        city: f.city ?? undefined,
        state: f.state ?? undefined,
        medium: f.medium ?? undefined,
        plan: f.plan ?? undefined,
        birthdayMonth: f.birthdayMonth ?? undefined,
        page: pg || pagination.current,
        pageSize: pgSize || pagination.pageSize,
      });

      const params = buildParams(currentFilters, page, pageSize);

      const { items, total } = await getClientsReport(params);
      setData(items || []);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total: total ?? items?.length ?? 0,
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar el reporte de clientes.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, tableFilters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = () => {
    fetchData(1, pagination.pageSize, sorter, filters);
  };

  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const params = {
        dateType: filters.dateType,
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD') ?? undefined,
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD') ?? undefined,
        client: filters.client?.trim() || undefined,
        gender: filters.gender ?? undefined,
        ageMin: filters.ageMin ?? undefined,
        ageMax: filters.ageMax ?? undefined,
        city: filters.city ?? undefined,
        state: filters.state ?? undefined,
        medium: filters.medium ?? undefined,
        plan: filters.plan ?? undefined,
        birthdayMonth: filters.birthdayMonth ?? undefined,
        page: 1,
        pageSize: 1000, // exporta hasta 1000 registros
      };

      const { items } = await getClientsReport(params);
      if (!items || items.length === 0) {
        message.warning('No hay datos para exportar');
        return;
      }

      const excelData = items.map((item, idx) => ({
        '#': idx + 1,
        Nombre: item.first_name || '-',
        Apellido: item.last_name || '-',
        Correo: item.email || '-',
        Género: item.gender || '-',
        Edad: item.age ?? '-',
        'Cumpleaños': isoToDMY(item.birth_date) || '-',
        Ciudad: item.city || '-',
        Estado: item.state || '-',
        Plan: item.plan || '-',
        'Fecha Suscripción': isoToDMY(item.subscription_date) || '-',
        'Fecha Registro': isoToDMY(item.registration_date) || '-',
        Medio: item.medium || '-',
      }));

      const columnWidths = [
        { wch: 5 },  // #
        { wch: 18 }, // Nombre
        { wch: 18 }, // Apellido
        { wch: 28 }, // Correo
        { wch: 12 }, // Género
        { wch: 8 },  // Edad
        { wch: 14 }, // Cumpleaños
        { wch: 18 }, // Ciudad
        { wch: 18 }, // Estado
        { wch: 18 }, // Plan
        { wch: 18 }, // Fecha Suscripción
        { wch: 18 }, // Fecha Registro
        { wch: 16 }, // Medio
      ];

      exportToExcel(excelData, columnWidths, 'Reporte Clientes', 'reporte_clientes');
    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    const reset = getDefaultFilters();
    setFilters(reset);
    // Recargar inmediatamente con filtros reseteados
    fetchData(1, pagination.pageSize, sorter, reset);
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Apellido',
      dataIndex: 'last_name',
      key: 'last_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Correo',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Género',
      dataIndex: 'gender',
      key: 'gender',
      width: 90,
    },
    {
      title: 'Edad',
      dataIndex: 'age',
      key: 'age',
      width: 70,
    },
    {
      title: 'Cumpleaños',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 100,
      render: (value) => isoToDMY(value) || '-',
    },
    {
      title: 'Ciudad',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Fecha Suscripción',
      dataIndex: 'subscription_date',
      key: 'subscription_date',
      width: 120,
      render: (value) => isoToDMY(value) || '-',
    },
    {
      title: 'Fecha Registro',
      dataIndex: 'registration_date',
      key: 'registration_date',
      width: 120,
      render: (value) => isoToDMY(value) || '-',
    },
    {
      title: 'Medio',
      dataIndex: 'medium',
      key: 'medium',
      width: 100,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ padding: 0, marginRight: 8 }}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
              Reporte Cliente
            </Title>
          </Space>
        }
        extra={
          <Tooltip title="Exportar a Excel">
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exporting}
              disabled={exporting}
              aria-label="Exportar a Excel"
            />
          </Tooltip>
        }
      >
        <Card
          type="inner"
          title="Filtros"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha de</span>
                <Select
                  placeholder="Seleccione..."
                  style={{ width: '100%' }}
                  value={filters.dateType}
                  onChange={(value) => handleFilterChange('dateType', value)}
                  options={DATE_TYPE_OPTIONS}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Inicio</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[0] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [date, filters.dateRange[1]]
                        : [date, null];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      // Si se limpia la fecha inicio, mantener fin si existe
                      const newRange = filters.dateRange?.[1]
                        ? [null, filters.dateRange[1]]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Fin</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[1] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [filters.dateRange[0], date]
                        : [null, date];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      // Si se limpia la fecha fin, mantener inicio si existe
                      const newRange = filters.dateRange?.[0]
                        ? [filters.dateRange[0], null]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                  disabledDate={(current) => {
                    // Deshabilitar fechas anteriores a la fecha inicio si existe
                    if (filters.dateRange?.[0]) {
                      return current && current < filters.dateRange[0].startOf('day');
                    }
                    return false;
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Cliente</span>
                <Input
                  placeholder="nombre, apellido, correo"
                  value={filters.client}
                  onChange={(e) => handleFilterChange('client', e.target.value)}
                  allowClear
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Género</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.gender}
                  onChange={(value) => handleFilterChange('gender', value)}
                  allowClear
                  options={genders.map(g => ({
                    value: g.id,
                    label: g.name,
                  }))}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Edad</span>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    min={0}
                    max={150}
                    value={filters.ageMin}
                    onChange={(value) => handleFilterChange('ageMin', value)}
                    style={{ width: '100%' }}
                  />
                  <span>-</span>
                  <InputNumber
                    placeholder="Max"
                    min={0}
                    max={150}
                    value={filters.ageMax}
                    onChange={(value) => handleFilterChange('ageMax', value)}
                    style={{ width: '100%' }}
                  />
                </Space>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Estado</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.state}
                  onChange={(value) => handleFilterChange('state', value)}
                  allowClear
                  options={states.map(state => ({
                    value: state.id,
                    label: state.name,
                  }))}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Ciudad</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.city}
                  onChange={(value) => handleFilterChange('city', value)}
                  allowClear
                  disabled={!filters.state || loadingMunicipalities}
                  loading={loadingMunicipalities}
                  options={municipalities.map(municipality => ({
                    value: municipality.id,
                    label: municipality.name,
                  }))}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Medio</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.medium}
                  onChange={(value) => handleFilterChange('medium', value)}
                  allowClear
                  options={MEDIUM_OPTIONS}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Compras</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.purchases}
                  onChange={(value) => handleFilterChange('purchases', value)}
                  allowClear
                  disabled
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Plan</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.plan}
                  onChange={(value) => handleFilterChange('plan', value)}
                  allowClear
                  options={plans.map(plan => ({
                    value: plan.id,
                    label: plan.name || plan.code,
                  }))}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Cumpleaños</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.birthdayMonth}
                  onChange={(value) => handleFilterChange('birthdayMonth', value)}
                  allowClear
                  options={MONTHS_OPTIONS}
                />
              </Space>
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                shape="circle"
                size="large"
              />
              <Button
                icon={<EditOutlined />}
                onClick={handleClearFilters}
                shape="circle"
                size="large"
              />
            </Space>
          </Row>
        </Card>

        <Table
          rowKey={(r) => r.id ?? r._id}
          columns={columns}
          dataSource={data}
          loading={loading}
          responsive={true}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
          }}
          onChange={onChange}
        />
      </Card>
    </div>
  );
};

export default ClientReportPage;
